/**
 * /w/:wsId/share — share this workspace with someone. Lists members and
 * pending email invites. Uses the same three roles as project invites
 * (`lead`, `contributor`, `viewer`) for a consistent UX; the server
 * translates `lead → admin`, `contributor → member`, `viewer → viewer`.
 *
 * The body of the screen lives in `WorkspaceMembersPanel` so the workspace
 * settings "Members" tab and this route share one implementation.
 */

import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuth } from '@/features/auth/authContext'
import { useWorkspaceMembers } from '@/features/workspaces/workspaces'
import { WorkspaceMembersPanel } from '@/components/workspace/WorkspaceMembersPanel'
import type { Workspace } from '@friday/shared/realtime'

export function WorkspaceMembersView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  const navigate = useNavigate()
  const { user } = useAuth()

  const wsQ = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: () => api<Workspace>(`/api/v1/workspaces/${wsId}`),
    enabled: !!wsId,
  })
  const membersQ = useWorkspaceMembers(wsId)

  const canManage =
    membersQ.data?.some((m) => m.userId === user?.id && (m.role === 'owner' || m.role === 'admin')) ?? false

  if (wsQ.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-surface-500">
        Loading workspace…
      </div>
    )
  }

  if (wsQ.error || !wsQ.data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">
        Failed to load workspace.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-surface-200 dark:border-surface-700 bg-white/60 dark:bg-surface-900/60 backdrop-blur px-4 md:pr-14 py-2">
        <button
          onClick={() => navigate({ to: '/w/$wsId', params: { wsId } })}
          className="rounded p-1 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800"
          title="Back to workspace"
        >
          ←
        </button>
        <h1 className="text-sm font-semibold">Share · {wsQ.data.name}</h1>
        {!canManage && (
          <span className="ml-2 rounded bg-surface-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-surface-600 dark:bg-surface-700 dark:text-surface-300">
            Read-only
          </span>
        )}
      </header>

      <div className="flex-1 overflow-auto p-4">
        <WorkspaceMembersPanel wsId={wsId} workspaceName={wsQ.data.name} />
      </div>
    </div>
  )
}
